import { isAudioWorkEnabled, runTrackedAudioWork } from '../../audioWorkBarrier';
import { captureUserContext } from '../../configure/user-context-facade';
import { getSafeErrorFields } from '../../errors';
import {
  dbV2,
  getPipelineMode,
  getRecordingRow,
  isMemoryOnlyWriteError,
  isRetainedChunk,
  isRetainedDispatchExhausted,
  ledgerDatabaseExists,
  listRetainedChunks,
  listUnpersistedRecordingRows,
  noteRetainedDispatch,
  peekRecordingRow,
  persistMirroredRow,
  runCanary,
  subscribeFenceClosed,
  withReopen,
} from '../../persistenceV2';
import { logger } from '../lib/logger';
import {
  clearDeferredRecovery,
  drainLedgerBacklogV2,
  isNormalizeOwed,
} from './hydrateV2';
import { hasInFlightBackupUploads } from './persistRegistry';
import { hasAnyLocalCaptureLease } from './recordingLockV2';
import { type GetState, type SetState } from './storeTypes';

export type RetryDrainTrigger =
  | 'upload-success'
  | 'recording-stop'
  | 'note-generation'
  | 'connectivity-regained'
  | 'liveness-expired';

interface TriggerCapability {
  /** May take the capture lock and scan the database for failed rows. */
  readonly unlocksBacklog: boolean;
  /** Runs now: the caller has no time left to wait one out. */
  readonly skipsDebounce: boolean;
}

/**
 * What each trigger is allowed to do, so a new one is a compile error rather
 * than a literal comparison somebody forgot to extend.
 */
const TRIGGERS = {
  'upload-success': { unlocksBacklog: false, skipsDebounce: false },
  'recording-stop': { unlocksBacklog: false, skipsDebounce: false },
  'note-generation': { unlocksBacklog: false, skipsDebounce: true },
  'connectivity-regained': { unlocksBacklog: true, skipsDebounce: false },
  // The activity grace that kept a row out of the last scan has run out.
  'liveness-expired': { unlocksBacklog: true, skipsDebounce: false },
} as const satisfies Record<RetryDrainTrigger, TriggerCapability>;

/** Backlog-unlocking outranks the rest: coalescing must never downgrade one. */
const unlocksBacklog = (trigger: RetryDrainTrigger | null): boolean =>
  trigger !== null && TRIGGERS[trigger].unlocksBacklog;

// Coalesces the burst of per-chunk upload-success pokes into one drain. Note
// generation skips it, so the wait before a note never pays this delay.
const DEBOUNCE_MS = 2_000;

let running = false;
let pendingTrigger: RetryDrainTrigger | null = null;
/** The armed debounce window's trigger; the timer reads it as it fires. */
let debounceWindow: { trigger: RetryDrainTrigger } | null = null;
let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let runPromise: Promise<void> | null = null;
let healFailureReported = false;
let ledgerProbeFailureReported = false;
let backlogPhaseFailureReported = false;
let userUnresolvedReported = false;
/** Recordings whose heal is already reported, so repeats stay quiet. */
const healRowFailuresReported = new Set<string>();
/**
 * A connectivity unlock the idle guards refused. The network usually returns
 * mid-recording, and only this session's later pokes will ever find capture
 * idle — dropping the unlock loses the backlog scan for the whole session.
 */
let backlogOwed = false;
/**
 * Bumped by every reset. A run that started before one speaks for a session
 * that is gone: it must neither clear the flags nor re-poke the next session's
 * drain.
 */
let generation = 0;

/**
 * One run's claim on the module state above. Every write happens after an await,
 * so a reset can land first and leave the run writing for a session that is gone.
 */
interface DrainRun {
  /** False once a reset has superseded this run. */
  readonly current: () => boolean;
}

const claimRun = (): DrainRun => {
  const runGeneration = generation;
  return { current: () => runGeneration === generation };
};

/** What this run could learn about the ledger database. */
type LedgerProbe = 'present' | 'absent' | 'unreadable' | 'unchecked';

/**
 * Dexie only swallows "no such database"; a dead IndexedDB throws. An absent
 * ledger is not a dead one, so the probe reports which, and the warn latches
 * rather than firing on every drain.
 */
const probeLedger = async (run: DrainRun): Promise<LedgerProbe> => {
  try {
    const exists = await ledgerDatabaseExists();
    if (run.current()) ledgerProbeFailureReported = false;
    return exists ? 'present' : 'absent';
  } catch (err) {
    if (run.current() && !ledgerProbeFailureReported) {
      ledgerProbeFailureReported = true;
      logger.warn({
        event_type: 'RETRY_DRAIN_DB_EXISTS_FAILED',
        details: getSafeErrorFields(err),
      });
    }
    return 'unreadable';
  }
};

/**
 * Fails open: this await sits ahead of the phase-3 verdict, so an escaping
 * rejection would abort the run and drop the unlock its trigger carried.
 */
const resolveOwnerRefId = async (): Promise<string | undefined> => {
  try {
    return (await captureUserContext.resolveCurrentUserRefId()) ?? undefined;
  } catch {
    return undefined;
  }
};

/** How one buffered chunk's turn ended. Every exit of the loop names one. */
type ChunkAttempt =
  | 'exhausted'
  | 'cut-off'
  | 'unknown-row'
  | 'foreign'
  | 'released'
  | 'deduped'
  | 'dispatched'
  | 'failed';

/**
 * Which endings cost the chunk one of its three dispatches. Only work another
 * dispatcher or the barrier still owns is free — burning budget there would
 * strand audio that is still on its way.
 */
const SPENDS_BUDGET = {
  exhausted: false,
  'cut-off': false,
  'unknown-row': true,
  foreign: true,
  released: false,
  deduped: false,
  dispatched: true,
  failed: true,
} as const satisfies Record<ChunkAttempt, boolean>;

/** Counts only — a per-phase tally the completion event reports. */
interface DrainOutcome {
  healed: number;
  dispatched: number;
  skipped: number;
  failed: number;
  backlogScanned: boolean;
  ledger: LedgerProbe;
}

/**
 * Which count each ending feeds. Explicit, not derived by subtraction: an upload
 * that threw is a failure, not a chunk held back.
 */
const ATTEMPT_TALLY = {
  exhausted: 'skipped',
  'cut-off': 'skipped',
  'unknown-row': 'skipped',
  foreign: 'skipped',
  released: 'skipped',
  deduped: 'skipped',
  dispatched: 'dispatched',
  failed: 'failed',
} as const satisfies Record<ChunkAttempt, 'dispatched' | 'skipped' | 'failed'>;

/**
 * Whether the backlog debt is settled. Only a probe that ANSWERED discharges it
 * — an empty answer is a scan that ran — so every failure on the way to one
 * keeps the scan owed for a later drain.
 */
type BacklogVerdict = 'settled' | 'owed';

interface BacklogPhaseInput {
  trigger: RetryDrainTrigger;
  outcome: DrainOutcome;
  ensureStoreUsable: () => Promise<boolean>;
  run: DrainRun;
  /** Phase 1 ran no canary, so nothing has opened the ledger yet. */
  probeBeforeCanary: boolean;
}

/** Work the scan and its tail would compete with, capture or another drain. */
const captureBusy = (get: GetState): boolean =>
  get().status === 'RECORDING' || hasAnyLocalCaptureLease() || hasInFlightBackupUploads();

/**
 * Phase 3 — database: cheap indexed probe on `failed`, full drain only when a
 * row exists. That drain reads and writes the ledger a live capture is writing
 * to, so only a recovered outage with capture idle may run it.
 */
const runBacklogPhase = async (
  set: SetState,
  get: GetState,
  { trigger, outcome, ensureStoreUsable, run, probeBeforeCanary }: BacklogPhaseInput
): Promise<BacklogVerdict> => {
  if (captureBusy(get)) {
    // Deferred, not dropped: the next drain that finds capture idle runs it.
    // First, so a latched debt buys no database work during a live capture.
    logger.debug({ event_type: 'RETRY_DRAIN_BACKLOG_DEFERRED', details: { trigger } });
    return 'owed';
  }
  if (probeBeforeCanary) {
    // An absent ledger has nothing to drain, and the canary below would CREATE
    // it — arming rollback drains on every later page load. A ledger that
    // cannot be asked has answered nothing, so a scan owed stays owed.
    outcome.ledger = await probeLedger(run);
    if (outcome.ledger !== 'present') {
      return outcome.ledger === 'unreadable' ? 'owed' : 'settled';
    }
  }
  if (!(await ensureStoreUsable())) return 'owed';
  try {
    // Pending too: a row a grace or the offline bound deferred is not failed.
    // Backups have their own rows, and an owed normalize pass has none at all.
    const [chunk, backup] = await withReopen(() =>
      Promise.all([
        dbV2.chunks.where('status').anyOf('pending', 'failed').first(),
        dbV2.backups.where('status').anyOf('pending', 'failed').first(),
      ])
    );
    if (!chunk && !backup && !isNormalizeOwed()) {
      if (run.current()) backlogPhaseFailureReported = false;
      return 'settled';
    }
    const userRefId = await captureUserContext.resolveCurrentUserRefId();
    if (!userRefId) {
      // A failed row is waiting and nothing will come back for it, so this exit
      // must be observable. Latched: the debt returns every later idle poke here.
      if (run.current() && !userUnresolvedReported) {
        userUnresolvedReported = true;
        logger.warn({ event_type: 'RETRY_DRAIN_USER_UNRESOLVED', details: { trigger } });
      }
      return 'owed';
    }
    if (run.current()) userUnresolvedReported = false;
    // Resolving is not scanning: another tab holding the capture lock, and the
    // drain's own swallowed failures, both come back here as a quiet skip.
    if (
      (await drainLedgerBacklogV2(set, get, userRefId, 'active-ledger')) !== 'scanned'
    ) {
      return 'owed';
    }
    if (run.current()) backlogPhaseFailureReported = false;
    outcome.backlogScanned = true;
    // A scan that skipped its normalize pass under a live recording is still owed.
    return isNormalizeOwed() ? 'owed' : 'settled';
  } catch (err) {
    // Same latch rule as the heal: a store that fails this probe fails it on
    // every drain the owed debt schedules.
    if (run.current() && !backlogPhaseFailureReported) {
      backlogPhaseFailureReported = true;
      logger.warn({
        event_type: 'RETRY_DRAIN_DB_PHASE_FAILED',
        details: getSafeErrorFields(err),
      });
    }
    return 'owed';
  }
};

/**
 * Heal, then buffer, then database — in that order, so a chunk flushed by the
 * heal is re-dispatched as a durable row instead of a memory blob. Triggers
 * coalesce: a poke during a run schedules exactly one follow-up.
 */
const runDrainPhases = async (
  set: SetState,
  get: GetState,
  trigger: RetryDrainTrigger,
  outcome: DrainOutcome,
  run: DrainRun
): Promise<void> => {
  // Every created row is mirrored and every chunk pokes the drain, so a healthy
  // capture would otherwise pay for a canary and a rewrite of the whole mirror
  // per chunk. Only memory the store has not taken is healing work.
  const retainedChunks = listRetainedChunks();
  const unpersistedRows = listUnpersistedRecordingRows();
  const hasHealWork = retainedChunks.length > 0 || unpersistedRows.length > 0;
  const scansBacklog = backlogOwed || unlocksBacklog(trigger);

  // Phase 1 — heal: a working store takes ownership of what the mirror still
  // owns. `storeUsable` reflects the canary alone; a row the store refuses is
  // isolated by the per-row heal below, not by this flag.
  let canaryRun = false;
  let storeUsable = false;
  const ensureStoreUsable = async (): Promise<boolean> => {
    if (canaryRun) return storeUsable;
    canaryRun = true;
    try {
      await runCanary();
      storeUsable = true;
      if (run.current()) healFailureReported = false;
    } catch (err) {
      // The buffer remains the only copy. An armed memory-only load refuses the
      // canary by design, and a dead store fails every 10-60s drain the same
      // way — report the transition into failure, not each repeat.
      if (run.current() && !isMemoryOnlyWriteError(err) && !healFailureReported) {
        healFailureReported = true;
        logger.warn({
          event_type: 'RETRY_DRAIN_HEAL_FAILED',
          details: getSafeErrorFields(err),
        });
      }
    }
    return storeUsable;
  };

  if (hasHealWork && (await ensureStoreUsable())) {
    // Per row, as phase 2 is per chunk: one row the store refuses must not
    // strand the rest of the mirror behind it.
    for (const row of unpersistedRows) {
      try {
        // A flush the stored row left nothing to write is not a heal; counting it
        // would report a flush on every drain for a debt that never moves.
        if ((await persistMirroredRow(row)).length > 0) outcome.healed += 1;
        if (run.current()) healRowFailuresReported.delete(row.recordingId);
      } catch (err) {
        // Drains fire per chunk, so a store refusing this row refuses it every
        // few seconds — report the transition into failure, not each repeat.
        if (!run.current() || healRowFailuresReported.has(row.recordingId)) continue;
        healRowFailuresReported.add(row.recordingId);
        logger.warn({
          event_type: 'RETRY_DRAIN_HEAL_ROW_FAILED',
          recording_id: row.recordingId,
          details: getSafeErrorFields(err),
        });
      }
    }
    if (outcome.healed > 0) {
      logger.info({
        event_type: 'MIRROR_FLUSHED_TO_STORE',
        details: { rows: outcome.healed },
      });
    }
  }

  // Phase 2 — buffer: re-dispatch retained chunks through the store action,
  // so `beginExclusiveUpload` dedupes against every other dispatcher. Resolved
  // once: the owner check below is per chunk, the identity behind it is not.
  const currentUserRefId =
    retainedChunks.length > 0 ? await resolveOwnerRefId() : undefined;
  for (const chunk of retainedChunks) {
    let attempt: ChunkAttempt;
    if (isRetainedDispatchExhausted(chunk)) {
      attempt = 'exhausted';
    } else {
      try {
        // A refused barrier never runs the callback, and that is a cutoff too.
        attempt =
          (await runTrackedAudioWork(async (context): Promise<ChunkAttempt> => {
            if (!isAudioWorkEnabled(context)) return 'cut-off';
            // Mirror first, database second — as the transport read does. Guessing
            // the transcription service would send the chunk to the wrong endpoint.
            const row = peekRecordingRow(chunk) ?? (await getRecordingRow(chunk));
            if (!row) {
              logger.warn({
                event_type: 'RETRY_DRAIN_RECORDING_UNKNOWN',
                recording_id: chunk.recordingId,
                details: { index: chunk.index },
              });
              return 'unknown-row';
            }
            // Defence in depth: a user switch purges the buffer, so a foreign row
            // here means it did not. An unresolved identity does not skip — the
            // buffer holds only what this session retained.
            if (currentUserRefId !== undefined && row.userRefId !== currentUserRefId) {
              logger.warn({
                event_type: 'RETRY_DRAIN_FOREIGN_RECORDING_SKIPPED',
                recording_id: chunk.recordingId,
                details: { index: chunk.index },
              });
              return 'foreign';
            }
            // Released while this snapshot aged: a concurrent drain already
            // uploaded it, and a second dispatch duplicates it in the note.
            if (!isRetainedChunk(chunk)) return 'released';
            logger.info({
              event_type: 'FAILED_CHUNK_REDISPATCHED',
              recording_id: chunk.recordingId,
              details: { index: chunk.index },
            });
            const dispatch = await get().processChunk(
              chunk,
              row.useTranscriptionServiceV2,
              context,
              'retry-drain'
            );
            return dispatch === 'deduped' ? 'deduped' : 'dispatched';
          }, 'chunk-upload')) ?? 'cut-off';
      } catch (err) {
        attempt = 'failed';
        logger.warn({
          event_type: 'RETRY_DRAIN_DISPATCH_FAILED',
          recording_id: chunk.recordingId,
          details: { index: chunk.index, ...getSafeErrorFields(err) },
        });
      }
    }
    outcome[ATTEMPT_TALLY[attempt]] += 1;
    if (SPENDS_BUDGET[attempt]) noteRetainedDispatch(chunk);
  }

  if (!scansBacklog) return;
  // A reset landed during the phases above: the scan would take the capture
  // lock and read the next session's database under this one's identity.
  if (!run.current()) return;
  // The debt's only write: a verdict, so no exit on the way to the probe can
  // discharge a scan that never ran.
  const verdict = await runBacklogPhase(set, get, {
    trigger,
    outcome,
    ensureStoreUsable,
    run,
    probeBeforeCanary: !hasHealWork,
  });
  if (run.current()) backlogOwed = verdict === 'owed';
};

// A drain past this has stalled on a lock or a hung upload; surface it.
const DRAIN_OVERRUN_MS = 60_000;

const runDrain = async (
  set: SetState,
  get: GetState,
  trigger: RetryDrainTrigger,
  run: DrainRun
): Promise<void> => {
  const outcome: DrainOutcome = {
    healed: 0,
    dispatched: 0,
    skipped: 0,
    failed: 0,
    backlogScanned: false,
    ledger: 'unchecked',
  };
  const startedAt = Date.now();
  // A no-op poke fires this on every upload success, so a drain that changed
  // nothing logs at debug; one that did work, or overran, stays at info.
  let overran = false;
  const overrunTimer = setTimeout(() => {
    overran = true;
    logger.warn({
      event_type: 'RETRY_DRAIN_OVERRUN',
      details: { trigger, afterMs: DRAIN_OVERRUN_MS },
    });
  }, DRAIN_OVERRUN_MS);
  try {
    await runDrainPhases(set, get, trigger, outcome, run);
  } finally {
    clearTimeout(overrunTimer);
    const didWork =
      outcome.healed > 0 ||
      outcome.dispatched > 0 ||
      outcome.failed > 0 ||
      outcome.skipped > 0 ||
      outcome.backlogScanned;
    // An owed backlog is the one no-work state still worth an info line: it is the
    // only record that recovery is still pending when debug is dropped in prod.
    logger[didWork || overran || backlogOwed ? 'info' : 'debug']({
      event_type: 'RETRY_DRAIN_DONE',
      details: { trigger, ...outcome, backlogOwed, durationMs: Date.now() - startedAt },
    });
  }
};

const startDrain = (set: SetState, get: GetState, trigger: RetryDrainTrigger): void => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;
  // A note-generation start cancels the scheduled timer; the backlog pass the
  // window it displaces unlocks must survive as the follow-up run.
  const displaced = debounceWindow?.trigger;
  if (displaced && unlocksBacklog(displaced) && !unlocksBacklog(trigger)) {
    pendingTrigger = displaced;
  }
  debounceWindow = null;
  running = true;
  const run = claimRun();
  logger.debug({ event_type: 'RETRY_DRAIN_START', details: { trigger } });
  // Catch last: the re-poke below runs inside the `finally`, and a throw there
  // would otherwise reject a promise nobody is left to handle.
  runPromise = runDrain(set, get, trigger, run)
    .finally(() => {
      // A reset happened mid-run: this run's flags and follow-up belong to a
      // session that is gone, and clearing `running` here would report the
      // next session's live drain as idle.
      if (!run.current()) return;
      running = false;
      if (pendingTrigger) {
        const next = pendingTrigger;
        pendingTrigger = null;
        pokeRetryDrain(set, get, next);
      }
    })
    .catch(() => undefined);
};

export const pokeRetryDrain = (
  set: SetState,
  get: GetState,
  trigger: RetryDrainTrigger
): void => {
  // Stop and connectivity poke on both pipelines; a legacy page must never
  // open dbV2, whose mere existence arms the rollback drain machinery.
  if (getPipelineMode() !== 'ledger') return;
  if (running) {
    // The follow-up replays what arrived mid-run, and coalescing must not
    // downgrade it.
    if (!unlocksBacklog(pendingTrigger)) pendingTrigger = trigger;
    return;
  }
  // Note generation is the last moment a retry can still reach the note, so
  // it skips the debounce the wait would otherwise have to sit through.
  if (TRIGGERS[trigger].skipsDebounce) {
    startDrain(set, get, trigger);
    return;
  }
  // The timer fires on the window's trigger, not on its first poke: a
  // connectivity poke arriving mid-window is the whole reason to drain.
  if (debounceWindow) {
    if (!unlocksBacklog(debounceWindow.trigger)) debounceWindow.trigger = trigger;
    return;
  }
  const scheduled = { trigger };
  debounceWindow = scheduled;
  debounceTimer = setTimeout(() => startDrain(set, get, scheduled.trigger), DEBOUNCE_MS);
};

/**
 * True while a drain is running or scheduled, so the note-generation wait can ask
 * before re-poking. A run a reset superseded reads false: it can neither dispatch
 * nor write state, so it is not a drain this session has to wait out.
 */
export const isRetryDrainActive = (): boolean => running || debounceTimer !== null;

/** Latches the scan as owed from outside a run; the next idle poke runs it. */
export const oweBacklogScan = (): void => {
  backlogOwed = true;
};

/**
 * Clears the single-flight and debounce state. A superseded run is left to
 * finish: the generation token stops its phase 3 and every module-state write.
 */
export const resetRetryDrain = (): void => {
  generation += 1;
  running = false;
  pendingTrigger = null;
  debounceWindow = null;
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = null;
  runPromise = null;
  healFailureReported = false;
  ledgerProbeFailureReported = false;
  backlogPhaseFailureReported = false;
  userUnresolvedReported = false;
  healRowFailuresReported.clear();
  backlogOwed = false;
  // Armed by this session's drain; fired later, it would poke the next one's.
  clearDeferredRecovery();
};

/**
 * Test-only. Awaits the in-flight run however many awaits its body holds, and
 * follows the chained run a coalesced trigger starts as the first one settles.
 */
export const __whenRetryDrainSettlesForTests = async (): Promise<void> => {
  while (runPromise) {
    const current = runPromise;
    await current;
    if (runPromise === current) runPromise = null;
  }
};

// A closed fence means logout is underway; a poke still inside the debounce
// window belongs to the session being torn down, not to the next one.
subscribeFenceClosed(() => resetRetryDrain());
